import { Mark, mergeAttributes } from "@tiptap/core";

const CLASSES = [
  "ql-font-roboto",
  "ql-font-manrope",
  "ql-font-serif",
  "ql-font-sansserif",
  "ql-font-monospace",
  "ql-font-abrilfatface",
];

function hasUsedClass(classes) {
  for (let i = 0; i < classes.length; i++) {
    const cssClass = classes[i];
    if (CLASSES.includes(cssClass)) {
      return true;
    }
  }
  return false;
}

function hasTextStyle(element) {
  return element.hasAttribute("style") || hasUsedClass(element.classList);
}

function hasNoInlineStyle(node, type) {
  return !node.marks.some(
    (mark) => mark.type === type && Object.values(mark.attrs).some(value => !!value),
  );
}

/**
 * This extension allows you to create text styles and include pre-defined classes for span-tags.
 * It is based on the official `TextStyle`-extension.
 * @see https://www.tiptap.dev/api/marks/text-style
 */
export const TextStyle = Mark.create({
  name: "textStyle",

  priority: 101,

  addOptions() {
    return {
      HTMLAttributes: {},
      mergeNestedSpanStyles: false,
    };
  },

  parseHTML() {
    return [
      {
        tag: "span,strong,em,s,u,sub,sup",
        getAttrs: (element) => {
          return hasTextStyle(element) ? {} : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      removeEmptyTextStyle: () => ({ tr }) => {
        const selection = tr.selection;
        // Gather all of the nodes within the selection range.
        // We would need to go through each node individually
        // to check if it has any inline style attributes.
        // Otherwise, calling commands.unsetMark(this.name)
        // removes everything from all the nodes
        // within the selection range.
        tr.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
          // Check if it's a paragraph element, if so, skip this node as we apply
          // the text style to inline text nodes only (span).
          if (node.isTextblock) {
            return true;
          }
          // Check if the node has no inline style attributes.
          // Filter out non-`textStyle` marks.
          if (hasNoInlineStyle(node, this.type)) {
            // Proceed with the removal of the `textStyle` mark for this node only
            tr.removeMark(pos, pos + node.nodeSize, this.type);
          }
        });
        return true;
      },
    };
  },
});
